import React, {useEffect, useState, useCallback, useMemo} from 'react';
import $ from "../../utils/datatables";
import {Link} from "react-router-dom";
import moment from "moment";
import { toast } from 'react-toastify';
import {useSelector, useDispatch} from "react-redux";
import feather from 'feather-icons';
import {GET_PRESEASON_CONTRACTS_BY_COMPANY_URL} from "../../api";
import {
    getCurrentUser,
    fetchAllPreseasonContractsByCompany,
    fetchAllPreseasonApplications,
    fetchAllFinalContractsByCompany,
    getProvinces
} from "../../api/web";
import {SET_AUTH_USER} from "../../stores/auth";
import {formatNumber, formatMoney, formatPricePerKg, formatMetricTonnesFromKg} from "../../utils/formatNumbers";
import {
    computePreseasonStats,
    collectPreseasonCropOptions,
    computePreseasonCropDrilldown,
    computeQuantityByProvinceFromContracts,
    filterPreseasonContractsByCrop,
    listingCropShareOnContract,
    agreedPricePerKgForFinalContract,
    approvedQtyKgForFinalContract,
} from "../../utils/preseasonStats";
import ZambiaMap from "../../components/ZambiaMap";

const PreSeasonContract = (props) => {
    const currencySymbol = 'ZMW ';

    const token = localStorage.getItem('jwtToken');
    const dispatch = useDispatch();
    const authState = useSelector((state) => state.auth.user);
    const user = authState?.user;
    const [pacraId, setPacraId] = useState(null);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [provinceNameById, setProvinceNameById] = useState({});
    const [finalizedQtyByContract, setFinalizedQtyByContract] = useState({});
    const [selectedProvince, setSelectedProvince] = useState(null);
    const [statsBundle, setStatsBundle] = useState({ contracts: [], applications: [], finalContracts: [] });
    const [selectedCropFilter, setSelectedCropFilter] = useState('all');
    const QUERY_MAP_PROVINCES = useMemo(() => ([
        'Central',
        'Copperbelt',
        'Eastern',
        'Luapula',
        'Lusaka',
        'Muchinga',
        'North-Western',
        'Northern',
        'Southern',
        'Western',
    ]), []);
    const canonicalProvinceName = useCallback((name) => {
        if (!name) return null;
        const n = String(name)
            .trim()
            .toLowerCase()
            .replace(/\s+/g, " ")
            .replace(/\bprovince\b/g, "")
            .trim();
        return QUERY_MAP_PROVINCES.find(
            (p) => p.toLowerCase().replace(/-/g, " ") === n || p.toLowerCase() === n.replace(/\s/g, "-")
        ) || null;
    }, [QUERY_MAP_PROVINCES]);

    useEffect(() => {
        if (user?.company?.pacra_id) {
            setPacraId(user.company.pacra_id);
            return;
        }
        if (!token) return;
        getCurrentUser()
            .then((data) => {
                if (data?.company?.pacra_id) {
                    setPacraId(data.company.pacra_id);
                    dispatch(SET_AUTH_USER({ user: data, token: authState?.token || token, isAuthenticated: true }));
                }
            })
            .catch(() => {});
    }, [token, user?.company?.pacra_id, authState?.token, dispatch]);

    const loadPreseasonStats = useCallback(async () => {
        if (!pacraId) return;
        setStatsLoading(true);
        try {
            const [contracts, applications, finalContracts] = await Promise.all([
                fetchAllPreseasonContractsByCompany(pacraId, token),
                fetchAllPreseasonApplications(token),
                fetchAllFinalContractsByCompany(pacraId, token),
            ]);
            const finalizedMap = finalContracts.reduce((acc, fc) => {
                const status = String(fc?.status || '').toLowerCase();
                if (status !== 'finalized') return acc;
                const snap = fc?.snapshot_json || {};
                const snapApp = snap?.application || {};
                const snapContract = snap?.contract || {};
                const contractId = snapContract?.id ?? fc?.application?.contract;
                if (contractId == null) return acc;
                const key = String(contractId);
                const qty = Number(snapApp?.approved_qty_kg ?? fc?.application?.approved_qty_kg ?? 0);
                acc[key] = (acc[key] || 0) + (Number.isFinite(qty) ? qty : 0);
                return acc;
            }, {});
            setFinalizedQtyByContract(finalizedMap);
            setStatsBundle({ contracts, applications, finalContracts });
            setStats(computePreseasonStats(contracts, applications, finalContracts));
        } catch (e) {
            toast.error('Failed to load preseason summary', { position: 'top-right', autoClose: 4000 });
            setFinalizedQtyByContract({});
            setStatsBundle({ contracts: [], applications: [], finalContracts: [] });
            setStats(computePreseasonStats([], [], []));
        } finally {
            setStatsLoading(false);
        }
    }, [pacraId, token]);

    const cropOptions = useMemo(
        () => collectPreseasonCropOptions(statsBundle.contracts, statsBundle.applications, statsBundle.finalContracts),
        [statsBundle]
    );

    const cropDrilldown = useMemo(() => {
        if (!selectedCropFilter || selectedCropFilter === 'all') return null;
        return computePreseasonCropDrilldown({
            contracts: statsBundle.contracts,
            applications: statsBundle.applications,
            finalContracts: statsBundle.finalContracts,
            finalizedQtyByContract,
            cropId: selectedCropFilter,
        });
    }, [statsBundle, finalizedQtyByContract, selectedCropFilter]);

    const displayStats = useMemo(() => {
        if (!stats) return null;
        if (!selectedCropFilter || selectedCropFilter === 'all' || !cropDrilldown) {
            return {
                amountContracted: stats.amountContracted,
                outstandingValue: stats.outstandingValue,
                totalOfferedQtyKg: stats.totalQuantityKg,
                totalQtyAvailable: stats.totalQtyAvailable,
                totalQtyReserved: stats.totalQtyReserved,
                totalPositionValue: stats.amountContracted + stats.outstandingValue,
                contractCount: stats.contractCount,
                applicationCount: stats.applicationCount,
                finalizedContractCount: stats.finalizedContractCount,
                priceLines: stats.priceLines,
                averageContractPrice: stats.averageContractPrice,
                averageContractedPrice: stats.averageContractedPrice,
            };
        }
        const d = cropDrilldown;
        const priceLines =
            d.latestListPricePerKg > 0 ? [{ label: d.label, pricePerKg: d.latestListPricePerKg }] : [];
        return {
            amountContracted: d.amountContractedCrop,
            outstandingValue: d.outstandingValueCrop,
            totalOfferedQtyKg: d.listedTotalQtyKg,
            totalQtyAvailable: d.listedAvailableQtyKg,
            totalQtyReserved: d.listedReservedQtyKg,
            totalPositionValue: d.amountContractedCrop + d.outstandingValueCrop,
            contractCount: d.contractsListingCropCount,
            applicationCount: d.applicationCountForCrop,
            finalizedContractCount: d.finalizedContractCount,
            priceLines,
            averageContractPrice: d.averageListedPricePerKg,
            averageContractedPrice: d.averageFinalizedPricePerKg,
        };
    }, [stats, selectedCropFilter, cropDrilldown]);

    const mixDisplay = useMemo(() => {
        if (!stats) return { top: [], more: 0 };
        const mix = stats.cropMixContribution ?? [];
        if (!selectedCropFilter || selectedCropFilter === 'all' || !cropDrilldown) {
            return { top: mix.slice(0, 5), more: Math.max(0, mix.length - 5) };
        }
        const d = cropDrilldown;
        const qty = d.cropMixQtyKg ?? 0;
        const val = d.cropMixValue ?? 0;
        if (qty <= 0 && val <= 0) return { top: [], more: 0 };
        return {
            top: [
                {
                    label: d.label,
                    qtyKg: qty,
                    value: val,
                    qtySharePct: 100,
                    valueSharePct: 100,
                },
            ],
            more: 0,
        };
    }, [stats, selectedCropFilter, cropDrilldown]);

    const quantityByProvince = useMemo(
        () =>
            computeQuantityByProvinceFromContracts(statsBundle.contracts, finalizedQtyByContract, {
                provinceNameById,
                canonicalProvinceName,
                cropId: selectedCropFilter,
            }),
        [
            statsBundle.contracts,
            finalizedQtyByContract,
            provinceNameById,
            canonicalProvinceName,
            selectedCropFilter,
        ]
    );

    const finalizedContractsRows = useMemo(() => {
        const list = Array.isArray(statsBundle.finalContracts) ? statsBundle.finalContracts : [];
        let rows = list.filter((fc) => String(fc?.status || '').toLowerCase() === 'finalized');
        if (selectedCropFilter && selectedCropFilter !== 'all') {
            rows = rows.filter((fc) => {
                const snapApp = fc?.snapshot_json?.application || {};
                const cid =
                    snapApp?.crop_id
                    ?? (typeof snapApp?.crop === 'object' ? snapApp?.crop?.id ?? snapApp?.crop?.pk : snapApp?.crop);
                const app = fc?.application;
                const fromApp =
                    app && (typeof app.crop === 'object' ? app.crop?.id ?? app.crop?.pk : app.crop);
                const resolved = cid ?? fromApp;
                return resolved != null && String(resolved) === String(selectedCropFilter);
            });
        }
        return [...rows].sort((a, b) => {
            const ta = new Date(a?.farmer_confirmed_at || a?.company_signed_at || a?.created_at || 0).getTime();
            const tb = new Date(b?.farmer_confirmed_at || b?.company_signed_at || b?.created_at || 0).getTime();
            return tb - ta;
        });
    }, [statsBundle.finalContracts, selectedCropFilter]);

    useEffect(() => {
        loadPreseasonStats();
    }, [loadPreseasonStats]);

    useEffect(() => {
        if (!statsLoading && stats != null) {
            feather.replace();
        }
    }, [statsLoading, stats, selectedCropFilter, displayStats]);

    useEffect(() => {
        if (!selectedCropFilter || selectedCropFilter === 'all') return;
        const ok = cropOptions.some((o) => String(o.id) === String(selectedCropFilter));
        if (!ok) setSelectedCropFilter('all');
    }, [cropOptions, selectedCropFilter]);

    useEffect(() => {
        getProvinces().then((res) => {
            if (res?.error || !Array.isArray(res?.payload)) return;
            const map = {};
            res.payload.forEach((province) => {
                const id = province?.id;
                const name = province?.name ?? province?.label ?? province?.province_name;
                if (id != null && name) {
                    map[String(id)] = name;
                }
            });
            setProvinceNameById(map);
        });
    }, []);

    useEffect(() => {
        if (!pacraId) return;

        const cropFilterId = selectedCropFilter && selectedCropFilter !== 'all' ? selectedCropFilter : null;
        const rowShare = (row) => listingCropShareOnContract(row, cropFilterId);
        const n = (v) => {
            const x = Number(v);
            return Number.isFinite(x) ? x : 0;
        };

        const language = {
            searchPlaceholder: 'Search',
            sSearch: '',
            lengthMenu: '_MENU_ items/page',
            processing: '<div class="spinner-border"></div><p class="mg-t-10 tx-12">Loading preseason contracts</p>',
        };

        const columns = [
            { data: 'raw' },
            { data: 'raw' },
            { data: 'raw' },
            { data: 'raw' },
            { data: 'raw' },
            { data: 'raw' },
            { data: 'raw' },
            { data: 'raw' },
            { data: 'raw' },
        ];

        const columnDefs = [
            {
                render(data) {
                    const statusMap = {
                        draft: '<span class="badge bg-secondary text-white">Draft</span>',
                        open: '<span class="badge bg-success text-white">Open</span>',
                        closed: '<span class="badge bg-dark text-white">Closed</span>',
                    };
                    return statusMap[data?.status] ?? (data?.status ?? '—');
                },
                targets: 0,
            },
            {
                render(data) {
                    return formatMetricTonnesFromKg(n(data?.total_quantity_kg) * rowShare(data));
                },
                targets: 1,
            },
            {
                render(data) {
                    return formatMetricTonnesFromKg(n(data?.reserved_qty_kg) * rowShare(data));
                },
                targets: 2,
            },
            {
                render(data) {
                    const finalizedKg =
                        data?.finalized_qty_kg
                        ?? data?.total_finalized_qty_kg
                        ?? data?.finalized_quantity_kg
                        ?? data?.finalized_kg
                        ?? finalizedQtyByContract[String(data?.id)];
                    return formatMetricTonnesFromKg(n(finalizedKg) * rowShare(data));
                },
                targets: 3,
            },
            {
                render(data) {
                    const explicitName = data?.province_name ?? data?.province_label;
                    if (explicitName) return explicitName;
                    const province = data?.province;
                    if (!province) return '—';
                    if (typeof province === 'object') {
                        return province?.name ?? province?.label ?? province?.province_name ?? '—';
                    }
                    const provinceKey = String(province);
                    if (provinceNameById[provinceKey]) {
                        return provinceNameById[provinceKey];
                    }
                    if (typeof province === 'number' || /^\d+$/.test(provinceKey)) {
                        return '—';
                    }
                    return provinceKey;
                },
                targets: 4,
            },
            {
                render(data) {
                    return data?.application_deadline ? moment(data.application_deadline).format('Do MMM, YYYY') : '—';
                },
                targets: 5,
            },
            {
                render(data) {
                    return data?.date_created ? moment(data.date_created).format('Do MMM, YYYY') : '—';
                },
                targets: 6,
            },
            {
                render(data) {
                    const lines = Array.isArray(data?.crop_prices) ? data.crop_prices : [];
                    if (lines.length === 0) return '—';
                    return lines
                        .map((line) => {
                            const cropLabel = line?.crop_name ?? line?.crop ?? 'Crop';
                            const priceLabel = line?.price_per_kg != null ? `${formatPricePerKg(line.price_per_kg)}/kg` : '—';
                            return `<div class="tx-12"><span class="tx-medium">${cropLabel}:</span> ${priceLabel}</div>`;
                        })
                        .join('');
                },
                targets: 7,
            },
            {
                render() {
                    return '<a href="#" class="nav-link view_contract"><i class="fa fa-eye"></i> View contract</a>';
                },
                targets: 8,
            },
        ];

        const baseConfig = {
            responsive: true,
            autoWidth: false,
            language,
            searching: true,
            lengthMenu: [[10], [10]],
            ordering: false,
            info: true,
            bFilter: false,
            pageLength: 10,
            columns,
            columnDefs,
        };

        let table;
        if (cropFilterId) {
            const rows = filterPreseasonContractsByCrop(statsBundle.contracts, cropFilterId).map((item) => ({ raw: item }));
            table = $('#preseason_contract_table').DataTable({
                ...baseConfig,
                serverSide: false,
                processing: false,
                data: rows,
            });
        } else {
            table = $('#preseason_contract_table').DataTable({
                ...baseConfig,
                processing: true,
                serverSide: true,
                ajax(data, callback) {
                    const page = Math.floor(data.start / data.length) + 1;
                    const url = GET_PRESEASON_CONTRACTS_BY_COMPANY_URL(pacraId, page, data.length);
                    $.ajax(url, {
                        type: 'GET',
                        headers: {
                            Authorization: token ? `Token ${token}` : '',
                        },
                        success(res) {
                            const rawList = Array.isArray(res) ? res : (res?.results ?? res?.data ?? []);
                            const result = rawList.map((item) => ({ raw: item }));

                            callback({
                                recordsTotal: res?.count ?? result.length,
                                recordsFiltered: res?.count ?? result.length,
                                data: result,
                            });
                        },
                        error(err) {
                            toast.error(err.statusText || 'Failed to load preseason contracts', {
                                position: 'top-right',
                                autoClose: 5000,
                            });
                            callback({ recordsTotal: 0, recordsFiltered: 0, data: [] });
                        },
                    });
                },
            });
        }

        table.on('click', '.view_contract', function (e) {
            e.preventDefault();
            const rowData = table.row($(this).closest('tr')).data();
            if (rowData && rowData.raw && rowData.raw.id) {
                props.history.push(`/preseason_contract/${rowData.raw.id}`);
            }
            return false;
        });

        return function cleanup() {
            if ($.fn.DataTable.isDataTable('#preseason_contract_table')) {
                $('#preseason_contract_table').DataTable().destroy();
            }
        };
    }, [
        pacraId,
        token,
        props.history,
        provinceNameById,
        finalizedQtyByContract,
        selectedCropFilter,
        statsBundle.contracts,
    ]);

    const reloadTable = () => {
        loadPreseasonStats();
        if ($.fn.DataTable.isDataTable('#preseason_contract_table')) {
            if (!selectedCropFilter || selectedCropFilter === 'all') {
                $('#preseason_contract_table').DataTable().ajax.reload(null, false);
            }
        }
    };

    const priceLinesAll = displayStats?.priceLines ?? [];
    const priceLinesShown = priceLinesAll.slice(0, 10);
    const priceLinesMore = Math.max(0, priceLinesAll.length - priceLinesShown.length);
    const cropMixTop = mixDisplay.top;
    const cropMixMore = mixDisplay.more;
    const isCropView = Boolean(selectedCropFilter && selectedCropFilter !== 'all' && cropDrilldown);
    const provinceRows = Object.entries(quantityByProvince)
        .map(([province, vals]) => ({
            province,
            qtyKg: vals?.qtyKg ?? 0,
            reservedKg: vals?.reservedKg ?? 0,
            finalizedKg: vals?.finalizedKg ?? 0,
            contracts: vals?.contracts ?? 0,
        }))
        .sort((a, b) => b.qtyKg - a.qtyKg);
    const visibleProvinceRows = selectedProvince
        ? provinceRows.filter((row) => row.province === selectedProvince)
        : provinceRows;
    const mapProvinceCounts = Object.fromEntries(
        provinceRows.map((row) => [row.province, row.qtyKg / 1000])
    );
    const maxProvinceQty = Math.max(...provinceRows.map((r) => r.qtyKg), 1);

    const preseasonIdForFinal = (fc) => {
        const sid = fc?.snapshot_json?.contract?.id;
        if (sid != null) return sid;
        const c = fc?.application?.contract;
        if (c && typeof c === 'object') return c.id ?? c.pk;
        return c ?? null;
    };

    const farmerNameForFinal = (fc) => {
        const f = fc?.snapshot_json?.application?.farmer;
        if (f && typeof f === 'object') {
            const n = [f.first_name, f.last_name].filter(Boolean).join(' ');
            if (n || f.username) return n || f.username;
            if (f.id != null) return `Farmer #${f.id}`;
        }
        const app = fc?.application;
        if (app?.farmer && typeof app.farmer === 'object') {
            const n = [app.farmer.first_name, app.farmer.last_name].filter(Boolean).join(' ');
            return n || app.farmer.username || (app.farmer.id != null ? `Farmer #${app.farmer.id}` : '—');
        }
        if (app?.farmer != null) return `Farmer #${app.farmer}`;
        return '—';
    };

    const cropNameForFinal = (fc) => {
        const snapApp = fc?.snapshot_json?.application;
        if (snapApp?.crop_name) return snapApp.crop_name;
        const c = snapApp?.crop;
        if (c && typeof c === 'object') return c.name ?? c.label ?? (c.id != null ? `Crop #${c.id}` : '—');
        const app = fc?.application;
        if (app?.crop && typeof app.crop === 'object') return app.crop.name ?? (app.crop.id != null ? `Crop #${app.crop.id}` : '—');
        if (app?.crop != null) return `Crop #${app.crop}`;
        return '—';
    };

    const finalContractStatusBadge = (status) => {
        const map = {
            awaiting_farmer: { cls: 'text-white', style: { backgroundColor: '#fd7e14' } },
            finalized: { cls: 'bg-success text-white', style: {} },
        };
        const c = map[status] || { cls: 'bg-secondary text-white', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{status || '—'}</span>;
    };

    return (
        <div className="content-body">
            <div className="container pd-x-0 pd-lg-x-10 pd-xl-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Preseason Contracts</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-0 tx-spacing--1">Preseason Contracts</h4>
                    </div>
                </div>

                {statsLoading && (
                    <div className="d-flex align-items-center mg-b-15 tx-12 tx-color-03">
                        <div className="spinner-border spinner-border-sm mg-r-10" role="status" />
                        <span>Loading summary…</span>
                    </div>
                )}

                {cropOptions.length > 0 && (
                    <div className="mg-b-15">
                        <div
                            className="d-flex flex-wrap align-items-center"
                            style={{ columnGap: '12px', rowGap: '10px' }}
                            role="toolbar"
                            aria-label="Filter preseason stats by crop"
                        >
                            <span className="tx-12 tx-medium tx-color-03 text-nowrap">Filter</span>
                            <button
                                type="button"
                                className={`btn btn-sm ${selectedCropFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setSelectedCropFilter('all')}
                            >
                                All
                            </button>
                            {cropOptions.map((o) => (
                                <button
                                    key={o.id}
                                    type="button"
                                    className={`btn btn-sm ${String(selectedCropFilter) === String(o.id) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setSelectedCropFilter(o.id)}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                        {isCropView ? (
                            <p className="tx-11 tx-color-03 mg-b-0 mg-t-8">
                                Stats, province totals, and the offers table use an equal share of each offer pool when several crops are listed.
                                Finalized contracts below are filtered to the selected crop.
                            </p>
                        ) : null}
                    </div>
                )}

                <div className="row mg-b-20" style={{ rowGap: '16px' }}>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-success tx-semibold mg-b-0">Amount contracted</h6>
                                <i data-feather="check-circle" className="tx-success" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{displayStats ? `${currencySymbol}${formatMoney(displayStats.amountContracted)}` : '—'}</h3>
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">
                                {isCropView ? 'Finalized snapshots for selected crop' : 'Based on finalized contract snapshots'}
                            </p>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-orange tx-semibold mg-b-0">Amount outstanding</h6>
                                <i data-feather="trending-up" className="tx-orange" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{displayStats ? `${currencySymbol}${formatMoney(displayStats.outstandingValue)}` : '—'}</h3>
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">
                                {isCropView ? 'Available (share) × list price for this crop' : 'Available (t) × avg crop price'}
                            </p>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-indigo tx-semibold mg-b-0">Total offered quantity</h6>
                                <i data-feather="archive" className="tx-indigo" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{displayStats ? formatMetricTonnesFromKg(displayStats.totalOfferedQtyKg) : '—'}</h3>
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">
                                {isCropView ? 'Attributed capacity on offers listing this crop' : 'Sum of offer totals (contract capacity)'}
                            </p>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-primary tx-semibold mg-b-0">Total quantity available</h6>
                                <i data-feather="package" className="tx-primary" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{displayStats ? formatMetricTonnesFromKg(displayStats.totalQtyAvailable) : '—'}</h3>
                            </div>
                            {isCropView ? (
                                <p className="tx-11 tx-color-03 mg-b-0 mt-auto">Attributed available capacity for this crop</p>
                            ) : null}
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-info tx-semibold mg-b-0">Total qty reserved</h6>
                                <i data-feather="lock" className="tx-info" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{displayStats ? formatMetricTonnesFromKg(displayStats.totalQtyReserved) : '—'}</h3>
                            </div>
                            {isCropView ? (
                                <p className="tx-11 tx-color-03 mg-b-0 mt-auto">Attributed reserved capacity for this crop</p>
                            ) : null}
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-purple tx-semibold mg-b-0">Total position value</h6>
                                <i data-feather="dollar-sign" className="tx-purple" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{displayStats ? `${currencySymbol}${formatMoney(displayStats.totalPositionValue)}` : '—'}</h3>
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">
                                {isCropView ? 'Amount contracted + amount outstanding for this crop' : 'Amount contracted + amount outstanding'}
                            </p>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-primary tx-semibold mg-b-0">Contracts</h6>
                                <i data-feather="layers" className="tx-primary" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{displayStats ? formatNumber(displayStats.contractCount) : '—'}</h3>
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">
                                {isCropView ? (
                                    <>
                                        Offers: {displayStats ? formatNumber(displayStats.contractCount) : '—'}
                                        {' · '}
                                        Applications: {displayStats ? formatNumber(displayStats.applicationCount) : '—'}
                                    </>
                                ) : (
                                    <>Preseason applications: {displayStats ? formatNumber(displayStats.applicationCount) : '—'}</>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-success tx-semibold mg-b-0">Finalized contracts</h6>
                                <i data-feather="check-square" className="tx-success" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{displayStats ? formatNumber(displayStats.finalizedContractCount) : '—'}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-warning tx-semibold mg-b-0">Current contract prices</h6>
                                <i data-feather="tag" className="tx-warning" />
                            </div>
                            <div className="tx-12 mg-b-5" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                                {!displayStats || priceLinesShown.length === 0 ? (
                                    <p className="tx-color-03 mg-b-0">No crop prices listed</p>
                                ) : (
                                    priceLinesShown.map((line, idx) => (
                                        <div key={`${line.label}-${idx}`} className="d-flex justify-content-between mg-b-5">
                                            <span className="tx-color-03">{line.label}</span>
                                            <span className="tx-medium">{formatPricePerKg(line.pricePerKg)}/kg</span>
                                        </div>
                                    ))
                                )}
                                {priceLinesMore > 0 && (
                                    <p className="tx-11 tx-color-03 mg-b-0 mg-t-5">+{priceLinesMore} more</p>
                                )}
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">
                                {isCropView ? 'Latest list price for this crop (newest offer first)' : 'Latest price per crop (newest contract first)'}
                            </p>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-secondary tx-semibold mg-b-0">Average contract price</h6>
                                <i data-feather="bar-chart-2" className="tx-secondary" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{displayStats ? `${formatPricePerKg(displayStats.averageContractPrice)}/kg` : '—'}</h3>
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">
                                {isCropView ? 'Attributed list value ÷ attributed total (t)' : 'Value ÷ total contract quantity (t)'}
                            </p>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-success tx-semibold mg-b-0">Average contracted price</h6>
                                <i data-feather="check-circle" className="tx-success" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{displayStats ? `${formatPricePerKg(displayStats.averageContractedPrice)}/kg` : '—'}</h3>
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">
                                {isCropView ? 'Finalized value ÷ finalized quantity for this crop' : 'Amount contracted ÷ finalized quantity'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="row g-3 mg-t-20 mg-b-20">
                    <div className="col-sm-12">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-info tx-semibold mg-b-0">Crop mix contribution</h6>
                                <i data-feather="pie-chart" className="tx-info" />
                            </div>
                            <div className="tx-12 mg-b-5" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                                {!displayStats || cropMixTop.length === 0 ? (
                                    <p className="tx-color-03 mg-b-0">No approved/finalized crop mix data</p>
                                ) : (
                                    cropMixTop.map((row, idx) => (
                                        <div key={`${row.label}-${idx}`} className="mg-b-8">
                                            <div className="d-flex justify-content-between">
                                                <span className="tx-medium">{row.label}</span>
                                                <span className="tx-color-03">{formatMetricTonnesFromKg(row.qtyKg)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between tx-11 tx-color-03">
                                                <span>Qty share: {Math.round(row.qtySharePct)}%</span>
                                                <span>Value share: {Math.round(row.valueSharePct)}%</span>
                                            </div>
                                        </div>
                                    ))
                                )}
                                {cropMixMore > 0 && (
                                    <p className="tx-11 tx-color-03 mg-b-0">+{cropMixMore} more crops</p>
                                )}
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">
                                {isCropView ? 'Selected crop (approved/finalized applications)' : 'Based on approved/finalized application quantities'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="row g-3 mg-t-10 mg-b-20">
                    <div className="col-sm-12">
                        <div className="card ht-100p">
                            <div className="card-header d-flex align-items-center justify-content-between">
                                <h6 className="mg-b-0">Quantity Per Province</h6>
                                <span className="tx-12 tx-color-03 text-end" style={{ maxWidth: 420 }}>
                                    {isCropView
                                        ? 'Attributed tonnes for the selected crop (equal split per offer).'
                                        : 'Preseason contract totals'}
                                </span>
                            </div>
                            <div className="card-body">
                                {provinceRows.length === 0 ? (
                                    <p className="tx-color-03 mg-b-0">No province quantity data</p>
                                ) : (
                                    <div className="row g-3 align-items-start">
                                        <div className="col-md-6">
                                            <div className="table-responsive">
                                                <table className="table table-borderless table-dashboard table-dashboard-one">
                                                    <thead>
                                                        <tr>
                                                            <th className="wd-50">Province</th>
                                                            <th className="wd-20 text-end">Total (t)</th>
                                                            <th className="wd-20 text-end">Reserved (t)</th>
                                                            <th className="wd-20 text-end">Finalized (t)</th>
                                                            <th className="wd-10 text-end">Contracts</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {visibleProvinceRows.map((row) => {
                                                            const percentage = (row.qtyKg / maxProvinceQty) * 100;
                                                            return (
                                                                <tr key={row.province}>
                                                                    <td className="tx-medium">{row.province}</td>
                                                                    <td className="text-end">
                                                                        <div className="d-flex align-items-center justify-content-end">
                                                                            <div className="progress flex-fill ht-4 mg-r-10" style={{ maxWidth: '120px' }}>
                                                                                <div
                                                                                    className="progress-bar bg-info"
                                                                                    role="progressbar"
                                                                                    style={{ width: `${percentage}%` }}
                                                                                ></div>
                                                                            </div>
                                                                            <span className="tx-medium">{formatMetricTonnesFromKg(row.qtyKg)}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="text-end tx-medium">{formatMetricTonnesFromKg(row.reservedKg)}</td>
                                                                    <td className="text-end tx-medium">{formatMetricTonnesFromKg(row.finalizedKg)}</td>
                                                                    <td className="text-end tx-medium">{formatNumber(row.contracts)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="rounded" style={{ maxWidth: 360, minHeight: 220, backgroundColor: '#f8f9fa' }}>
                                                <ZambiaMap
                                                    selectedProvince={selectedProvince}
                                                    onSelect={(name) => setSelectedProvince((prev) => (prev === name ? null : name))}
                                                    canonicalProvinceNames={QUERY_MAP_PROVINCES}
                                                    provinceCounts={mapProvinceCounts}
                                                    compact
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row row-xs">
                    <div className="col-md-12 mg-t-10">
                        <div className="d-flex flex-column" style={{ gap: '24px' }}>
                        <div className="card">
                            <div className="card-header d-flex align-items-start justify-content-between flex-wrap gap-2">
                                <div>
                                    <h6 className="mg-b-0">Preseason Contracts</h6>
                                    {isCropView ? (
                                        <p className="tx-11 tx-color-03 mg-b-0 mg-t-5">
                                            {"Offers that list this crop only. Tonne columns show this crop's attributed share."}
                                        </p>
                                    ) : null}
                                </div>
                                <div className="d-flex tx-18 flex-shrink-0">
                                    <a href="#" onClick={(e) => { e.preventDefault(); reloadTable(); }} className="link-03 lh-0" role="button"><i className="icon ion-md-refresh"></i></a>
                                </div>
                            </div>
                            <div className="card-body table-responsive">
                                <table id="preseason_contract_table" className="table table-hover" style={{width: "100%"}}>
                                    <thead>
                                        <tr>
                                            <th className="wd-8p">Status</th>
                                            <th className="wd-10p">Total (t)</th>
                                            <th className="wd-10p">Reserved (t)</th>
                                            <th className="wd-10p">Total finalized (t)</th>
                                            <th className="wd-12p">Province</th>
                                            <th className="wd-12p">Application Deadline</th>
                                            <th className="wd-12p">Date Created</th>
                                            <th className="wd-24p">Price lines</th>
                                            <th className="wd-12p">Action</th>
                                        </tr>
                                    </thead>
                                </table>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-header d-flex align-items-start justify-content-between flex-wrap gap-2">
                                <div>
                                    <h6 className="mg-b-0">Finalized contracts</h6>
                                    {isCropView ? (
                                        <p className="tx-11 tx-color-03 mg-b-0 mg-t-5">
                                            Showing finalized rows for the selected crop only.
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                            <div className="card-body table-responsive">
                                {finalizedContractsRows.length === 0 ? (
                                    <p className="tx-color-03 mg-b-0">No finalized preseason contracts yet.</p>
                                ) : (
                                    <table className="table table-hover table-sm mg-b-0">
                                        <thead>
                                            <tr>
                                                <th>Preseason offer</th>
                                                <th>Application</th>
                                                <th>Farmer</th>
                                                <th>Crop</th>
                                                <th className="text-end">Approved (t)</th>
                                                <th className="text-end">Price / kg</th>
                                                <th className="text-end">Value (ZMW)</th>
                                                <th>Status</th>
                                                <th>Company signed</th>
                                                <th>Farmer confirmed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {finalizedContractsRows.map((fc) => {
                                                const priceKg = agreedPricePerKgForFinalContract(fc);
                                                const qtyKg = approvedQtyKgForFinalContract(fc);
                                                const value = priceKg > 0 ? qtyKg * priceKg : 0;
                                                const offerId = preseasonIdForFinal(fc);
                                                return (
                                                    <tr key={fc.id ?? `${fc.application?.id}-${fc.application?.contract}`}>
                                                        <td>
                                                            {offerId != null ? (
                                                                <Link to={`/preseason_contract/${offerId}`} className="link-02">
                                                                    #{offerId}
                                                                </Link>
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </td>
                                                        <td>#{fc.application?.id ?? '—'}</td>
                                                        <td>{farmerNameForFinal(fc)}</td>
                                                        <td>{cropNameForFinal(fc)}</td>
                                                        <td className="text-end">{formatMetricTonnesFromKg(qtyKg)}</td>
                                                        <td className="text-end">{`${formatPricePerKg(priceKg)}/kg`}</td>
                                                        <td className="text-end">{`${currencySymbol}${formatMoney(value)}`}</td>
                                                        <td>{finalContractStatusBadge(fc.status)}</td>
                                                        <td>
                                                            {fc.company_signed_at
                                                                ? moment(fc.company_signed_at).format('Do MMM, YYYY HH:mm')
                                                                : '—'}
                                                        </td>
                                                        <td>
                                                            {fc.farmer_confirmed_at
                                                                ? moment(fc.farmer_confirmed_at).format('Do MMM, YYYY HH:mm')
                                                                : '—'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreSeasonContract;
